import { useParams } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import { Loader2 } from 'lucide-react';
import { CampaignOrdersCountChart } from '@/components/campaign-orders-count-chart';
import { CampaignOrdersRevenueChart } from '@/components/campaign-orders-revenue-chart';
import { Card, CardContent } from '@/components/ui/card';
import { ChartCardHeader } from '@/components/metrics/chart-card-header';
import { SectionHeader } from '@/components/metrics/section-header';
import { NoDataPlaceholder } from '@/components/metrics/no-data-placeholder';
import { CampaignMetricsGrid } from '@/components/metrics/campaign-metrics-grid';
import { CampaignsPeriodTable } from '@/components/metrics/campaigns-period-table';
import { PushFunnelCard } from '@/components/metrics/push-funnel-card';
import { formatCurrency, formatNumber } from '@/components/metrics/formatters';
import {
  ANALYTICS_SYNC_ID,
  type CampaignsSectionProps,
} from '@/components/metrics/metric-sections.types';

export function CampaignsSection({
  campaignPerformance,
  isLoadingCampaignPerformance,
  isLoadingDataSufficiency,
  dateFilterLabel,
  currency,
  campaignsSufficiency,
  granularity,
  chartRange,
  periodCampaigns,
  campaignOrdersChart,
  isLoadingCampaignOrdersChart,
  openDrilldown,
}: CampaignsSectionProps) {
  const { LL, locale } = useI18nContext();
  const { tenantSlug } = useParams();
  const analyticsSyncId = ANALYTICS_SYNC_ID;

  return (
    <div>
      <SectionHeader
        title={LL.metricsPage.sections.campaigns.title()}
        description={LL.metricsPage.sections.campaigns.description()}
      />
      <CampaignMetricsGrid
        campaignPerformance={campaignPerformance}
        isLoadingCampaignPerformance={isLoadingCampaignPerformance}
        isLoadingDataSufficiency={isLoadingDataSufficiency}
        dateFilterLabel={dateFilterLabel}
        currency={currency}
        campaignsSufficiency={campaignsSufficiency}
      />
      {/* The period's funnel, between the indicators and the charts (FUN-04). */}
      <div className="mt-8">
        <PushFunnelCard
          tenantSlug={tenantSlug ?? ''}
          campaignPerformance={campaignPerformance}
          currency={currency}
          hideValues={campaignsSufficiency?.canDisplay === false}
        />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 min-[1200px]:grid-cols-2 min-[1800px]:grid-cols-3">
        <Card>
          <ChartCardHeader
            title={LL.metricsPage.charts.campaignOrdersCount.title()}
            total={formatNumber(
              campaignPerformance?.totalOrderPaidCount,
              locale
            )}
            helpKey="campaignOrdersCountChart"
            dataSufficiency={campaignsSufficiency}
            isLoadingDataSufficiency={isLoadingDataSufficiency}
          />
          <CardContent>
            <div className="flex flex-col gap-4 w-full h-[200px]">
              {isLoadingCampaignOrdersChart ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : isLoadingDataSufficiency ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : campaignsSufficiency && !campaignsSufficiency.canDisplay ? (
                <NoDataPlaceholder fullHeight />
              ) : (
                <CampaignOrdersCountChart
                  range={chartRange}
                  granularity={granularity}
                  points={campaignOrdersChart?.points ?? []}
                  syncId={analyticsSyncId}
                  syncMethod="value"
                  onBucketClick={
                    openDrilldown
                      ? (bucketStart) =>
                          openDrilldown('campaign_orders_count', bucketStart)
                      : undefined
                  }
                />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <ChartCardHeader
            title={LL.metricsPage.charts.campaignOrdersRevenue.title()}
            total={formatCurrency(
              campaignPerformance?.totalRevenue,
              locale,
              currency
            )}
            helpKey="campaignOrdersRevenueChart"
            dataSufficiency={campaignsSufficiency}
            isLoadingDataSufficiency={isLoadingDataSufficiency}
          />
          <CardContent>
            <div className="flex flex-col gap-4 w-full h-[200px]">
              {isLoadingCampaignOrdersChart ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : isLoadingDataSufficiency ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : campaignsSufficiency && !campaignsSufficiency.canDisplay ? (
                <NoDataPlaceholder fullHeight />
              ) : (
                <CampaignOrdersRevenueChart
                  range={chartRange}
                  campaigns={periodCampaigns}
                  granularity={granularity}
                  points={campaignOrdersChart?.points ?? []}
                  currency={currency}
                  syncId={analyticsSyncId}
                  syncMethod="value"
                  onBucketClick={
                    openDrilldown
                      ? (bucketStart) =>
                          openDrilldown('campaign_orders_revenue', bucketStart)
                      : undefined
                  }
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="mt-4">
        <CampaignsPeriodTable
          tenantSlug={tenantSlug ?? ''}
          campaigns={periodCampaigns}
          currency={currency}
        />
      </div>
    </div>
  );
}
